import { BigDecimal } from "@solomon-labs/bigdecimal";
import { PanelMenu, PanelTrigger } from "@solomon-labs/design/components/ui";
import React, { useCallback, useRef, useState } from "react";

import { IndicationUnit } from "../components/Metrics/IndicationUnit";
import { useUnsavedChanges } from "../components/UnsavedChangesProvider";
import { useTransactionPhase } from "../hooks/transactions/useTransactionPhase";
import {
    useSimMeasureCardHeight,
    useSimMode,
    useSwapOrchestration
} from "../sim";
import { collapseStyle } from "../utils/collapseStyle";

import { FeeFooter } from "./primitives/FeeFooter";
import { SwapContent } from "./swap/SwapContent";
import type { TokenSelectorProps } from "./types/tokenSelector";

interface TradeWidgetProps {
    /**
     * Externally controlled mode. When provided, the widget hides its
     * internal Swap/Stake tab bar — the caller is expected to render its
     * own tab UI (e.g. page-level tabs on `USDvPositionsPage`). When
     * omitted, the widget manages its own mode via internal state and
     * renders the tab bar inline (legacy standalone usage).
     */
    mode?: "swap" | "stake";
    /** Called when the widget requests an Earn or Swap mode change.
     *  Required when `mode` is controlled. */
    onModeChange?: (mode: "swap" | "stake") => void;
    /** Initial mode when the widget is uncontrolled. Lets a caller open
     *  the widget with Swap focused without taking over mode state.
     *  Ignored when `mode` is provided. Defaults to `"stake"` (Earn). */
    initialMode?: "swap" | "stake";
    /** Optional Jupiter-backed token picker (forwarded to SwapContent).
     *  See BuyWidget's prop for the same rationale — apps/main passes
     *  its production `TokenSelector`, sim consumers omit it. */
    TokenSelectorComponent?: React.ComponentType<TokenSelectorProps>;
    /** Optional render-prop for the Earn-mode panel content. apps/main
     *  passes its production `EarnContent` (Yaas portfolio summary,
     *  rewards-destination flow); sim consumers omit it and the Earn
     *  tab body falls back to a thin placeholder. EarnContent pulls
     *  `@/pages/Yaas/...` so we keep it in apps/main — the sim
     *  showcase only ever shows the Swap tab anyway. */
    EarnContentComponent?: React.ComponentType;
    /** Sim-only: forces the simulated swap to render the given side
     *  (Buy = send USDC / receive USDv; Sell = send USDv / receive
     *  USDC). Opt-in — when omitted the sim defaults to Buy and
     *  production callers (real `useSwap` injected) ignore it
     *  entirely. Also seeds the active Buy/Sell sub-tab. */
    simSwapSide?: "buy" | "sell";
}

/**
 * @version 1.2.0
 * @published 2026-04-16
 * @history
 *   1.2.0 — Added optional `mode` + `onModeChange` props. When `mode` is
 *           provided, the widget is fully controlled and the internal
 *           Swap/Stake tab bar is hidden. Used by `USDvPositionsPage`
 *           where page-level tabs drive the mode.
 *   1.1.0 — Wired the widget to real swap/stake hooks. Split the original
 *           662-line monolith into a folder so each piece mirrors the v2
 *           wired version 1:1. The Swap mode now consumes `useSwap`
 *           (Jupiter quote, balance, execute) lifted to the shell so the
 *           Price Impact / Fee / Network Cost footer can share the same
 *           state as the form. Stake / Unstake / Withdraw sub-tabs each
 *           consume their own staking hooks. Visuals (tabs, fade-in
 *           entrance, ember dot, animated reveal) are unchanged from
 *           1.0.0.
 *   1.0.0 — Initial publish from prototype/pages/USDv/USDvPositionsPage.tsx.
 *           Right-side widget on the logged-in USDv page. Top-level Swap /
 *           Stake tabs gate between `SwapContent` (buy/sell/bridge) and
 *           `StakeContent` (stake/unstake/withdraw sub-tabs with the
 *           ember border edge animation and count-up earnings box).
 */
export function TradeWidget({
    mode: controlledMode,
    onModeChange,
    initialMode = "stake",
    TokenSelectorComponent,
    EarnContentComponent,
    simSwapSide
}: TradeWidgetProps = {}): React.ReactElement {
    // Default to "stake" (renders as "Earn" in the tab UI) so the first
    // visible tab is the default-selected one. Caller can override the
    // initial mode for deep-link entry points (e.g. ?panel=swap).
    const [internalMode, setInternalMode] = useState<"swap" | "stake">(
        initialMode
    );
    const mode = controlledMode ?? internalMode;
    // Internal mode flips (uncontrolled / desktop path) go through
    // the unsaved-changes guard so switching between Earn ↔ Swap
    // while EarnContent has dirty form state prompts a confirm.
    // Controlled callers (page-level tabs) own their own guarding
    // via USDvPositionsPage's handleTabChange.
    const { tryNavigate } = useUnsavedChanges();
    const setMode = (next: "swap" | "stake"): void => {
        if (onModeChange) onModeChange(next);
        else tryNavigate(() => setInternalMode(next));
    };
    const showInternalTabs = controlledMode === undefined;
    // `simSwapSide` is sim-only chrome read by the sim
    // orchestrator; the real `useSwap` ignores the extra key. Only
    // pass it through when set so the production args stay `{}`.
    const swap = useSwapOrchestration(simSwapSide ? { simSwapSide } : {});
    const cardRef = useRef<HTMLDivElement>(null);
    const sim = useSimMode();

    // Stagger delay from page load (widget starts at 500ms)
    const base = 500;
    // Content entrance stagger. Production staggers ~1s after page load for
    // a choreographed reveal; in the sim showcase there's no page context,
    // so a long stagger reads as a "loading" pause (blank card under the
    // tabs). Collapse it to a near-instant reveal in sim mode.
    const contentDelayMs = sim.enabled ? 60 : base + 500;

    const handleSubmit = useCallback((): Promise<void> => {
        return swap.handleSubmit(cardRef.current?.offsetHeight);
    }, [swap]);

    // Phase derivation lifted to the widget so we can switch the card lock
    // and keep SwapContent's UI in sync via the prop.
    // Buy mode opts into the postSuccess celebration window (1500ms after
    // success) — Sell mode plateaus at success.
    const swapHasInput = new BigDecimal(swap.sendAmount || "0").gt(0);
    const swapPhase = useTransactionPhase({
        isPending: swap.swapView === "pending",
        isSuccess: swap.swapView === "success",
        executeState: swap.executeState,
        hasValue: swapHasInput,
        postSuccessDelay: swap.mode === "buy" ? 1500 : undefined
    });
    const isSwapPostSuccess = swapPhase === "postSuccess";

    // Card lock — only Buy/Sell still drives a height lock now that Earn
    // mode no longer has a card-height-changing form. Hard lock during
    // pending/processing/success/error so collapsing inputs + expanding
    // receipt stay inside a fixed bounding box.
    //
    // In sim mode, prefer the widget-measured filled height over the
    // orchestrator's constant fallback. See `useSimMeasureCardHeight`
    // — the measurement runs ~450ms after `hasInput` flips true so the
    // FeeFooter expand animation has settled and we capture the true
    // pre-submit form view height (not the empty-state mount height).
    const simCardHeight = useSimMeasureCardHeight(cardRef, swapHasInput);
    const effectiveCardHeight = simCardHeight ?? swap.cardHeight;
    const cardStyle: React.CSSProperties | undefined =
        mode === "swap"
            ? swap.swapView !== "form" && effectiveCardHeight
                ? isSwapPostSuccess
                    ? { minHeight: effectiveCardHeight }
                    : { height: effectiveCardHeight, overflow: "hidden" }
                : undefined
            : undefined;

    return (
        <div className="flex flex-col w-full lg:w-[28rem]">
            {/* Swap/Stake tabs — visible immediately.
                Hidden when `mode` is controlled externally (page-level tabs). */}
            {showInternalTabs && (
                <div>
                    <PanelMenu
                        value={mode}
                        onValueChange={(v) => setMode(v as "swap" | "stake")}
                        fullWidth
                    >
                        {(
                            [
                                { value: "stake", label: "Earn" },
                                { value: "swap", label: "Swap" }
                            ] as const
                        ).map((item) => {
                            const isSelected = mode === item.value;
                            return (
                                <PanelTrigger
                                    key={item.value}
                                    value={item.value}
                                    size="lg"
                                    interaction="lifted"
                                    className="flex-1"
                                >
                                    {/* `gap-1.5` + `pairWith="body"` follow
                                        the brand-ui IndicationUnit pairing
                                        table (claude-docs/design-system/
                                        brand-ui.md#indicationunit-pairing) —
                                        PanelTrigger size="lg" renders as
                                        `body` text, so the dot needs size 5,
                                        alignPt 9, and a 6px gap to land on
                                        the cap-height of the label. */}
                                    <span className="flex items-start gap-1.5 w-full justify-center">
                                        {/* Leading marker — ember dot when
                                            selected; transparent placeholder
                                            otherwise so width stays stable.
                                            Placeholder mirrors the dot's
                                            5px size + 9px top offset. */}
                                        {isSelected ? (
                                            <IndicationUnit
                                                variant="top"
                                                pairWith="body"
                                            />
                                        ) : (
                                            <span className="shrink-0 pt-[9px]">
                                                <span className="block size-[5px] bg-transparent" />
                                            </span>
                                        )}
                                        <span>{item.label}</span>
                                        <span className="shrink-0 pt-[9px]">
                                            <span className="block size-[5px] bg-transparent" />
                                        </span>
                                    </span>
                                </PanelTrigger>
                            );
                        })}
                    </PanelMenu>
                </div>
            )}

            {/* Widget card. Padding: `p-0` on mobile (flush with the page edges), `p-7` (28px) on desktop.
                During pending+ the card is hard-locked to the captured height
                (overflow hidden) so the input collapse + receipt expand stay
                inside a fixed bounding box; on postSuccess the lock relaxes
                to `minHeight` during the post-success transition.

                One fade-in wrapper holds both SwapContent and the FeeFooter
                so they share a single entrance. The FeeFooter wrapper itself
                is then a clean `collapseStyle` element (no `animation`
                declaration), matching the showcase BuyFlow footer pattern
                (TradeWidgetShowcase.tsx:371) — combining `animation` and
                `collapseStyle` on the same element caused a visible "instant
                collapse then re-shrink" glitch on click. */}
            <div
                ref={cardRef}
                className="group/widget bg-surface-raised p-0 md:p-7 flex flex-col"
                style={cardStyle}
            >
                <div
                    className="flex flex-col gap-3 md:gap-6 flex-1"
                    style={{
                        opacity: 0,
                        animation: `fade-in 200ms ease-out ${contentDelayMs}ms forwards`
                    }}
                >
                    {mode === "swap" ? (
                        <SwapContent
                            swap={swap}
                            phase={swapPhase}
                            onSubmit={handleSubmit}
                            TokenSelectorComponent={TokenSelectorComponent}
                        />
                    ) : EarnContentComponent ? (
                        <EarnContentComponent />
                    ) : (
                        // Sim-only fallback — apps/main injects the
                        // real EarnContent. The fallback never renders
                        // in the showcase (cells use initialMode="swap").
                        <div className="text-foreground-neutral p-8 text-center">
                            Earn mode not available in sim.
                        </div>
                    )}

                    {mode === "swap" && (
                        <div
                            // Parent animation wrapper is `gap-3 md:gap-6` —
                            // gap-eating margin needs to match per breakpoint
                            // (12 mobile / 24 desktop). Hardcoding
                            // `gapCompensation: 24` over-eats by 12px on
                            // mobile, releasing extra free space to the
                            // bordered card's flex-1 and producing a visible
                            // empty band below the receipt. The conditional
                            // `-mt-3 md:-mt-6` className handles this
                            // responsively; collapseStyle just transitions
                            // margin-top.
                            className={
                                swap.swapView !== "form" ? "-mt-3 md:-mt-6" : ""
                            }
                            style={collapseStyle(swap.swapView !== "form", {
                                maxHeight: 100,
                                gapCompensationFromClass: true,
                                duration: 400,
                                opacityDuration: 300
                            })}
                        >
                            <FeeFooter
                                priceImpact={swap.priceImpactDisplay}
                                fee={swap.routingFeeDisplay}
                                networkCost={swap.networkFeeDisplay}
                                loading={swap.feeFooterLoading}
                            />
                        </div>
                    )}

                    {/* Earn mode renders without a footer — each position
                        card in `EarnContent` carries its own activation
                        affordance, and there's no global Network Cost row. */}
                </div>
            </div>
        </div>
    );
}
